import { apiBaseUrl, getToken } from './client';

export interface SseEvent {
  event?: string;
  id?: string;
  data: string;
}

export interface SseStreamOptions {
  path: string;
  since?: number;
  signal?: AbortSignal;
  onEvent: (evt: SseEvent) => void;
  onError?: (err: unknown) => void;
}

/**
 * fetch + ReadableStream based SSE reader.
 *
 * EventSource cannot attach Authorization headers, so we parse text/event-stream manually.
 * Supports both the `since` query param and the `Last-Event-ID` header for resumption —
 * backend honors either (see processing_tasks_routes_stream.py).
 */
export async function openSseStream(opts: SseStreamOptions): Promise<void> {
  const token = getToken();
  const url = new URL(opts.path.startsWith('http') ? opts.path : `${apiBaseUrl}${opts.path}`);
  if (opts.since !== undefined) url.searchParams.set('since', String(opts.since));

  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.since !== undefined) headers['Last-Event-ID'] = String(opts.since);

  const res = await fetch(url.toString(), { method: 'GET', headers, signal: opts.signal });
  if (!res.ok || !res.body) {
    throw new Error(`SSE connect failed: HTTP ${res.status}`);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;

      let idx: number;
      while ((idx = indexOfBoundary(buffer)) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx).replace(/^(\r?\n){1,2}/, '');
        const evt = parseEvent(raw);
        if (evt) opts.onEvent(evt);
      }
    }
    if (buffer.trim()) {
      const evt = parseEvent(buffer);
      if (evt) opts.onEvent(evt);
    }
  } catch (err) {
    if ((err as { name?: string }).name !== 'AbortError') {
      opts.onError?.(err);
      throw err;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

function indexOfBoundary(s: string): number {
  const a = s.indexOf('\n\n');
  const b = s.indexOf('\r\n\r\n');
  if (a === -1) return b;
  if (b === -1) return a;
  return Math.min(a, b);
}

function parseEvent(block: string): SseEvent | null {
  const lines = block.split(/\r?\n/);
  let eventName: string | undefined;
  let id: string | undefined;
  const dataParts: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const valueRaw = colon === -1 ? '' : line.slice(colon + 1);
    const value = valueRaw.startsWith(' ') ? valueRaw.slice(1) : valueRaw;

    if (field === 'event') eventName = value;
    else if (field === 'id') id = value;
    else if (field === 'data') dataParts.push(value);
    else if (field === 'retry') {
      /* ignore — we manage our own reconnect */
    }
  }
  if (!dataParts.length) return null;
  return { event: eventName, id, data: dataParts.join('\n') };
}
